import { arrayMove } from '@dnd-kit/sortable';
import { create } from 'zustand';
import { defaultRoute, findRoute } from '@/config/app';
import { clearRouteDrafts } from '@/utils/formDraft';

export type AppTab = {
  id: string;
  label: string;
  path: string;
  closable: boolean;
};

export type CloseTabsAction = 'current' | 'left' | 'right' | 'others' | 'all';

type TabState = {
  tabs: AppTab[];
  activeTabId: string;
  openTab: (tab: AppTab) => void;
  activateTab: (id: string) => void;
  closeTab: (id: string) => string;
  closeTabs: (action: CloseTabsAction, targetId: string) => string;
  reorderTabs: (activeId: string, overId: string) => void;
  resetTabs: () => void;
};

const dashboardTab: AppTab = { ...defaultRoute };

export function tabsAfterClose(
  tabs: AppTab[],
  action: CloseTabsAction,
  targetId: string,
) {
  const targetIndex = tabs.findIndex((tab) => tab.id === targetId);
  if (targetIndex < 0) return tabs;
  return tabs.filter((tab, index) => {
    if (!tab.closable) return true;
    switch (action) {
      case 'current':
        return tab.id !== targetId;
      case 'left':
        return index >= targetIndex;
      case 'right':
        return index <= targetIndex;
      case 'others':
        return tab.id === targetId;
      case 'all':
        return false;
      default:
        return true;
    }
  });
}

// 母版不持久化打开的标签；只清理本项目旧标签缓存，避免恢复旧演示页面。
try {
  window.sessionStorage.removeItem('react-admin.tabs.v1');
} catch {
  // 存储不可用不影响内存中的标签导航。
}

export const useTabStore = create<TabState>()((set, get) => ({
  tabs: [dashboardTab],
  activeTabId: dashboardTab.id,
  openTab: (tab) => {
    const route = findRoute(tab.path.split(/[?#]/)[0]);
    if (!route) return;
    const nextTab = { ...route, path: tab.path };
    set((state) => ({
      tabs: state.tabs.some((item) => item.id === route.id)
        ? state.tabs.map((item) => (item.id === route.id ? nextTab : item))
        : [...state.tabs, nextTab],
      activeTabId: route.id,
    }));
  },
  activateTab: (id) => {
    if (get().tabs.some((tab) => tab.id === id)) set({ activeTabId: id });
  },
  closeTab: (id) => get().closeTabs('current', id),
  closeTabs: (action, targetId) => {
    const { tabs, activeTabId } = get();
    const targetIndex = tabs.findIndex((tab) => tab.id === targetId);
    const currentTab = tabs.find((tab) => tab.id === activeTabId) ?? dashboardTab;
    if (targetIndex < 0) return currentTab.path;
    const nextTabs = tabsAfterClose(tabs, action, targetId);
    tabs
      .filter((tab) => !nextTabs.includes(tab))
      .forEach((tab) => {
        clearRouteDrafts(tab.id);
      });
    const nextActive =
      nextTabs.find((tab) => tab.id === activeTabId) ??
      (action === 'current'
        ? (nextTabs[targetIndex - 1] ?? nextTabs[targetIndex])
        : nextTabs.find((tab) => tab.id === targetId)) ??
      dashboardTab;
    set({ tabs: nextTabs, activeTabId: nextActive.id });
    return nextActive.path;
  },
  reorderTabs: (activeId, overId) => {
    if (activeId === overId) return;
    set((state) => {
      const from = state.tabs.findIndex((tab) => tab.id === activeId);
      const to = state.tabs.findIndex((tab) => tab.id === overId);
      if (from <= 0 || to <= 0) return state;
      return { tabs: arrayMove(state.tabs, from, to) };
    });
  },
  resetTabs: () => set({ tabs: [dashboardTab], activeTabId: dashboardTab.id }),
}));
